import React, { Component } from 'react'
import ReactDOM from 'react-dom'

import moment from 'moment'
import Icon from 'components/icon/icon'
import SectionControls from 'components/controls/section-controls'

import {
	updateTask,
	generateGantt
} from 'state/actions'

import AlertBanner from 'components/alerts/alert-banner'

import { connect } from 'react-redux'

import GanttChart from './gantt-chart'

import SmoothCollapse from 'react-smooth-collapse'

import GanttControls from './gantt-controls'

import {
	momentToApiTimeFormat,
	randomTaskName,
	doSort,
	getPrimaryColumnValue,
} from 'utils'

import copy from 'fast-copy'

import { MainContext } from 'context'

class ProjectTasksGantt extends Component {
	state = {
		zoomLevelIndex: 3,
		datesHeight: 60,
		isDragging: false,
		tempTask: null,
		isShowingGanttTasks: true,
		shouldSort: true,
		shouldHideDateless: true,
		hasSetInitialScrollLeft: false
	}

	ganttData = {
		totalDateRange: 0,
		rowData: []
	}

	zoomLevels = [2, 3, 5, 7, 10, 20, 30, 40]

	chartComponentRef = null
	blockPan = false // if the date range is 100% of the width, don't allow panning.
	chartWidth = 0

	componentDidMount = _ => {
		window.requestAnimationFrame(_ => {
			this.setChartWidth()
			this.generateGanttData()
			this.updateDatesHeight()

			this.setZoom('fit')
		})
	}

	componentDidUpdate = _ => {
		this.setChartWidth()
		this.updateDatesHeight()
	}

	updateDatesHeight = _ => {
		const datesHeight = this.chartComponentRef ? (this.chartComponentRef.chartTrackRef.datesRef.getBoundingClientRect().height + 1) : 0

		if (datesHeight && datesHeight !== this.state.datesHeight) {
			this.setState({
				datesHeight
			})
		}
	}

	getCellWidth = _ => {
		return this.zoomLevels[this.state.zoomLevelIndex]
	}

	getPositionAndMeasurements = _ => {
		if (!this.chartComponentRef) {
			return null
		}

		const data = {
			scrollLeft: this.chartComponentRef.state.scrollLeft,
			containerWidth: this.chartRefs.outer.getBoundingClientRect().width,
			totalWidth: this.chartRefs.inner.getBoundingClientRect().width
		}

		return data
	}

	chartRefs = {}

	setZoom = direction => {
		const currentZoom = this.state.zoomLevelIndex
		let newZoom = currentZoom

		let positionAndMeasurements = this.getPositionAndMeasurements()

		if (!positionAndMeasurements) {
			return
		}

		switch (direction) {
			case 'up':
				// zoom in
				if (this.zoomLevels[(newZoom + 1)]) {
					newZoom++
				}
				break
			case 'down':
				// zoom out
				if (this.zoomLevels[(newZoom - 1)]) {
					newZoom--
				}
				break
			case 'fit':
				const divWidth = this.chartWidth
				const totalTaskDateRange = this.ganttData.totalTaskDateRange
				const minColumnWidth = Math.floor(divWidth / totalTaskDateRange)

				let newZoomLevel = -1

				this.zoomLevels.forEach((columnWidth, index) => {
					// if the NEXT level is bigger than minColumnWidth (and newZoomLevel hasn't been set), newZoomLevel is the current index
					if (newZoomLevel < 0) {
						if (this.zoomLevels[(index + 1)]) {
							const nextWidth = this.zoomLevels[(index + 1)]

							if (nextWidth > minColumnWidth) {
								newZoomLevel = index
							}
						} else {
							newZoomLevel = index
						}
					}
				})

				if (newZoomLevel > -1) {
					newZoom = newZoomLevel
				}

				break
		}

		const scrollCentre = positionAndMeasurements.scrollLeft + (positionAndMeasurements.containerWidth / 2)
		const percentCentreFromLeft = positionAndMeasurements.totalWidth / scrollCentre

		this.setState({
			zoomLevelIndex: newZoom
		}, _ => {
			window.requestAnimationFrame(_ => {
				// this just checks that the scroll isn't outside the date bounds
				positionAndMeasurements = this.getPositionAndMeasurements()

				if (!positionAndMeasurements) {
					return
				}

				let newScrollLeft = 0

				if (!this.state.hasSetInitialScrollLeft) {
					if (this.ganttData.earliestMoment) {
						const firstItemOffsetLeft = (positionAndMeasurements.totalWidth / this.ganttData.totalDateRange) * this.ganttData.earliestTaskMoment.diff(this.ganttData.earliestMoment, 'days')

						newScrollLeft = firstItemOffsetLeft

						this.setState({
							hasSetInitialScrollLeft: true
						})
					}
				} else {
					newScrollLeft = Math.round((positionAndMeasurements.totalWidth / percentCentreFromLeft) - (positionAndMeasurements.containerWidth / 2))

					if (newScrollLeft < 0) {
						newScrollLeft = 0
					}

					const newScrollRight = newScrollLeft + positionAndMeasurements.containerWidth
					const deficit = (newScrollRight - positionAndMeasurements.totalWidth)

					if (deficit > 0) {
						newScrollLeft -= deficit
					}
				}

				this.chartComponentRef.setScrollLeft(newScrollLeft)
			})
		})
	}

	setChartWidth = _ => {
		if (this.chartRefs.outer) {
			this.chartWidth = this.chartRefs.outer.getBoundingClientRect().width
		}
	}

	generateGanttData = _ => {
		const {
			currentProject,
			milestonesArray,
			loading
		} = this.props

		const {
			shouldSort,
			shouldHideDateless
		} = this.state

		let rowData = []

		let earliestMoment = currentProject.start_date ? moment(currentProject.start_date) : moment()
		let latestMoment = currentProject.end_date ? moment(currentProject.end_date) : null
		let earliestTaskMoment = moment()
		let minimumDates = 0
		let relevantUnits = 0
		let totalDateRange = 0
		let totalTaskDateRange = 0
		const cellWidth = this.getCellWidth()
		const divWidth = this.chartWidth

		// AAH damnit, to properly calculate earliestMoment AND task indices we need to loop twice
		milestonesArray.forEach(milestone => {
			milestone.tasks.forEach(loopedTask => {
				const taskDateObject = getPrimaryColumnValue('date', loopedTask, currentProject)

				if (taskDateObject.start) {
					const startMoment = moment(taskDateObject.start)

					if (startMoment.isBefore(earliestMoment)) {
						earliestMoment = startMoment
					}

					if (startMoment.isBefore(earliestTaskMoment)) {
						earliestTaskMoment = startMoment
					}

					if (!latestMoment || startMoment.isAfter(latestMoment)) {
						latestMoment = startMoment
					}
				}

				if (taskDateObject.end) {
					const endMoment = moment(taskDateObject.end)

					if (endMoment.isBefore(earliestMoment)) {
						earliestMoment = endMoment
					}

					if (endMoment.isBefore(earliestTaskMoment)) {
						earliestTaskMoment = endMoment
					}

					if (!latestMoment || endMoment.isAfter(latestMoment)) {
						latestMoment = endMoment
					}
				}
			})
		})

		if (!latestMoment || !earliestMoment) {
			return
		}

		// give them a little breathing room.
		earliestMoment.subtract(3, 'days')
		latestMoment.add(3, 'days')

		milestonesArray.forEach(milestone => {
			let taskData = []

			let earliestMilestoneMoment = null
			let latestMilestoneMoment = null

			if (loading) {
				for (let index = 0; index < milestone.taskCount; index++) {
					taskData.push({
						id: milestone.id + '_loading_' + index,
						name: randomTaskName(),
						valid: false
					})

				}
			} else {
				milestone.tasks.forEach(loopedTask => {
					// if we're currently dragging a task, use the temp one instead.
					const task = (this.state.tempTask && this.state.tempTask.id == loopedTask.id) ? this.state.tempTask : loopedTask

					const taskDateObject = getPrimaryColumnValue('date', task, currentProject)
					const loopedTaskDateObject = getPrimaryColumnValue('date', loopedTask, currentProject)

					let startDate = taskDateObject.start
					let endDate = taskDateObject.end

					if (!startDate && endDate) {
						startDate = endDate
					}

					if (startDate && !endDate) {
						endDate = startDate
					}

					if (shouldHideDateless && !loopedTaskDateObject.start && !loopedTaskDateObject.end) {
						return
					}

					const startMoment = moment(startDate)
					const endMoment = moment(endDate)

					if (startMoment.isAfter(endMoment)) {
						return
					}

					if (startDate) {
						if (!earliestMilestoneMoment || startMoment.isBefore(earliestMilestoneMoment)) {
							earliestMilestoneMoment = startMoment
						}
					}

					if (endDate) {
						if (!latestMilestoneMoment || endMoment.isAfter(latestMilestoneMoment)) {
							latestMilestoneMoment = endMoment
						}
					}

					let taskObject = {
						id: task.id,
						name: task.name,
						start: taskDateObject.start ? taskDateObject.start : taskDateObject.end,
						end: taskDateObject.end ? taskDateObject.end : taskDateObject.start,
						users: getPrimaryColumnValue('people', task, currentProject),
						valid: false
					}

					if (startDate && endDate) {
						let startIndex = 0
						let endIndex = 0

						startIndex = startMoment.diff(earliestMoment, 'days')
						endIndex = startIndex + endMoment.diff(startMoment, 'days') + 1

						if (startIndex == endIndex) {
							endIndex++
						}

						taskObject.startIndex = startIndex
						taskObject.endIndex = endIndex
						taskObject.valid = true
					}

					taskData.push(taskObject)
				})
			}

			if (taskData.length) {
				if (shouldSort) {
					taskData = doSort(taskData, 'date', 'start', false)
				}

				rowData.push({
					name: milestone.name,
					id: milestone.id,
					taskData,
					startIndex: earliestMilestoneMoment ? earliestMilestoneMoment.diff(earliestMoment, 'days') : null,
					endIndex: latestMilestoneMoment ? (latestMilestoneMoment.diff(earliestMoment, 'days') + 1) : null,
					earliestMoment: earliestMilestoneMoment,
					latestMoment: latestMilestoneMoment
				})
			}
		})

		relevantUnits = latestMoment.diff(earliestMoment, 'days') + 2
		minimumDates = Math.ceil(divWidth / cellWidth)

		totalTaskDateRange = latestMoment.diff(earliestTaskMoment, 'days') + 2

		if (minimumDates < relevantUnits) {
			this.blockPan = false
			totalDateRange = relevantUnits
		} else {
			this.blockPan = true
			totalDateRange = minimumDates
		}

		let shouldForceUpdate = false

		if (!this.ganttData.totalDateRange && totalDateRange) {
			shouldForceUpdate = true
		}

		this.ganttData = {
			totalDateRange,
			totalTaskDateRange,
			rowData,
			earliestMoment,
			earliestTaskMoment
		}

		if (shouldForceUpdate) {
			this.forceUpdate()
		}
	}

	getColumnData = _ => {
		const ganttData = this.ganttData

		const {
			zoomLevelIndex
		} = this.state

		const cellWidth = this.getCellWidth()

		const {
			totalDateRange,
			earliestMoment
		} = ganttData

		let dayIterator = 0
		let momentIterator = moment(earliestMoment)
		let unit = 'day'

		if (zoomLevelIndex <= 2) {
			unit = 'month'
		} else if (zoomLevelIndex <= 5) {
			unit = 'week'
		} else {
			unit = 'day'
		}

		const columnData = {
			unit,
			columns: []
		}

		let sanityIterator = 0

		while ((dayIterator < totalDateRange) && sanityIterator < 300) {
			sanityIterator += 1

			let daysInUnit = 1
			let isNotFrontTruncated = true

			if (unit == 'month') {
				daysInUnit = momentIterator.daysInMonth()

				isNotFrontTruncated = momentIterator.date() == 1
			} else if (unit == 'week') {
				daysInUnit = 7
			}

			let daysVisible = 0

			if (isNotFrontTruncated) {
				if (unit == 'day') {
					daysVisible = 1
				} else {
					if ((dayIterator + daysInUnit) < totalDateRange) {
						daysVisible = daysInUnit
					} else {
						// days in month is irrelevant if it's full to the end
						daysVisible = totalDateRange - dayIterator
					}
				}
			} else {
				if (unit == 'month') {
					daysVisible = (daysInUnit - momentIterator.date()) + 1 // we're adding 1 cause the date itself is visible
				} else {
					daysVisible = (daysInUnit - momentIterator.day()) + 1 // we're adding 1 cause the date itself is visible
				}
			}

			dayIterator += daysVisible

			const column = {
				moment: moment(momentIterator),
				width: (daysVisible * cellWidth)
			}

			columnData.columns.push(column)

			momentIterator = momentIterator.add(1, unit)

			if (unit == 'month') {
				momentIterator = momentIterator.startOf(unit)
			}
		}

		return columnData
	}

	handleResizeTask = (taskId, dir, deltaX) => {
		if (deltaX) {
			const {
				allTasks,
				currentProject,
				updateTask
			} = this.props

			const task = allTasks[taskId]

			const normalisedDelta = deltaX / this.getCellWidth()

			let tempTask = {
				...task,
			}

			const taskDateObject = getPrimaryColumnValue('date', task, currentProject)
			const column = getPrimaryColumnValue('date', task, currentProject, false, true)

			let newDateObject = taskDateObject ? {
				...taskDateObject
			} : {
				start: null,
				end: null
			}

			// we aren't using an 'else' or anything here, cause if it has no dates, then it won't be shown.
			if (!taskDateObject.start) {
				newDateObject.start = taskDateObject.end
			}

			if (!taskDateObject.end) {
				newDateObject.end = taskDateObject.start
			}

			switch (dir) {
				case 'left':
					newDateObject.start = momentToApiTimeFormat(moment(taskDateObject.start).subtract(normalisedDelta, 'days'))
					break
				case 'right':
					newDateObject.end = momentToApiTimeFormat(moment(taskDateObject.end).add(normalisedDelta, 'days'))
					break
			}

			const columnData = tempTask.column_data ? {
				...tempTask.column_data,
				[column.id]: newDateObject
			} : {
				[column.id]: newDateObject
			}

			tempTask.column_data = copy(columnData)

			updateTask(tempTask)

			this.forceUpdate()
		}
	}

	toggleCollapse = expandOrContract => {
		let milestoneIds = []

		this.ganttData.rowData.forEach(milestoneId => {
			milestoneIds.push(milestoneId.id)
		})

		this.props.toggleCollapseMilestone(this.props.userData, expandOrContract, milestoneIds)
	}

	isMilestoneCollapsed = id => {
		return this.props.userData.collapsed_milestones ? this.props.userData.collapsed_milestones.indexOf(id) !== -1 : false
	}

	toggleCheckbox = type => {
		switch (type) {
			case 'sort':
				this.setState({
					shouldSort: !this.state.shouldSort
				})
				break
			case 'hide':
				this.setState({
					shouldHideDateless: !this.state.shouldHideDateless
				})
				break
		}
	}

	exportToExcel = _ => {
		const {
			generateGantt,
			currentProject
		} = this.props

		generateGantt(this.ganttData, currentProject)
	}

	isAllDataEmpty = _ => {
		const {
			loading
		} = this.props

		if (loading) {
			return false
		}

		let isAllDataEmpty = true

		const {
			rowData
		} = this.ganttData

		rowData && rowData.some(row => {
			if (row.earliestMoment || row.latestMoment) {
				isAllDataEmpty = false

				return true
			}
		})

		return isAllDataEmpty
	}

	render() {
		const {
			allTasks,
			toggleView,
			toggleState,
			toggleGantts,
			loading,
			userData,
			toggleCollapseMilestone,
			currentProject
		} = this.props

		const {
			isShowingGanttTasks,
			zoomLevelIndex,
			shouldSort,
		} = this.state

		const buttonProps = [
			{
				size: 'big',
				level: 'low',
				label: 'Task list',
				callback: toggleGantts,
				imgIcon: 'list'
			},
			{
				size: 'big',
				level: 'low',
				label: 'Export to Excel',
				callback: this.exportToExcel,
				icon: 'table'
			}
		]

		let canExpand = true // you only can't expand if it's all open
		let canCollapse = true // you only can't collapse if everything's shut

		if (userData.collapsed_milestones) {
			const totalMilestones = (this.ganttData && this.ganttData.rowData.length) ? this.ganttData.rowData.length : 0
			const totalCollapsedMilestones = userData.collapsed_milestones.length

			if (totalMilestones == totalCollapsedMilestones) {
				canCollapse = false
			} else if (!totalCollapsedMilestones) {
				canExpand = false
			}
		}

		const ganttControls = (
			<GanttControls
				toggleCollapse={this.toggleCollapse}
				setZoom={this.setZoom}
				zoomLevelIndex={zoomLevelIndex}
				zoomLevels={this.zoomLevels}
				canExpand={canExpand}
				canCollapse={canCollapse}
			/>
		)

		const {
			datesHeight,
		} = this.state

		let cellWidth = this.getCellWidth()

		this.generateGanttData()

		const columnData = this.getColumnData()

		const checkboxProps = [
			{
				label: 'Sort tasks by date',
				isChecked: shouldSort,
				callback: _ => { this.toggleCheckbox('sort') }
			},
			/*{
				label: 'Hide dateless tasks',
				isChecked: shouldHideDateless,
				callback: _ => {this.toggleCheckbox('hide')}
			} */
		]

		const isAllDataEmpty = this.isAllDataEmpty()

		if (isAllDataEmpty) {
			buttonProps[1].disabled = true
		}

		return (
			<div className="project-tasks project-tasks-gantt" id="project-tasks">
				<SectionControls
					buttons={buttonProps}
					toggleState={toggleState}
					toggleChangeCallback={toggleView}
					toggleOptions={null}
					buttonShelfHidden={false}
					customShelfContent={ganttControls}
					checkboxProps={checkboxProps}
				/>
				{isAllDataEmpty ?
					<AlertBanner
						title={`Nothing to show here.`}
						body={`Your tasks don't have any start or end dates, so there's nothing to chart.`}
					/>
					:
					<div className="gantt-container">
						<div className="gantt-content">
							<div className="gantt-body">
								<ul className="gantt-legend" style={{
									marginTop: datesHeight + 'px'
								}}>
									{this.ganttData.rowData && this.ganttData.rowData.map((row, rowIndex) => {
										const isCollapsed = this.isMilestoneCollapsed(row.id)

										let className = 'gantt-milestone'

										if (isCollapsed) {
											className += ' collapsed'
										}

										return (
											<li className={className} key={rowIndex}>
												<h3 onClick={_ => {
													toggleCollapseMilestone(userData, row.id)
												}}>
													{row.name}
													<Icon name="angle-up" />
												</h3>
												<SmoothCollapse expanded={!isCollapsed}>
													<ul className="gantt-task-list">
														{
															row.taskData.map((task, index) => {
																let spacerClassName = 'gantt-row-spacer'

																if (loading) {
																	spacerClassName += ' loading'
																}

																return (
																	<li key={index}>
																		<div className={spacerClassName} style={{
																			animationDelay: index / 20 + 's'
																		}}>
																			{task.name}
																		</div>
																	</li>
																)
															})
														}
													</ul>
												</SmoothCollapse>
											</li>
										)
									})}
								</ul>
								<GanttChart
									currentProject={currentProject}
									chartRefs={this.chartRefs}
									ganttData={this.ganttData}
									cellWidth={cellWidth}
									allTasks={allTasks}
									updateTask={this.props.updateTask}
									handleResizeTask={this.handleResizeTask}
									ref={ref => this.chartComponentRef = ref}
									blockPan={this.blockPan}
									isShowingGanttTasks={isShowingGanttTasks}
									isMilestoneCollapsed={this.isMilestoneCollapsed}
									zoomLevelIndex={zoomLevelIndex}
									columnData={columnData}
									collapsedMilestones={userData.collapsed_milestones ? userData.collapsed_milestones : {}}
								/>
							</div>
						</div>
					</div>
				}
			</div>
		);
	}
}

export default connect(null, {
	updateTask,
	generateGantt
})(ProjectTasksGantt)

ProjectTasksGantt.contextType = MainContext